// apps/merchant-v3/src/components/BackToTop.tsx
//
// Floating "back to top" button. Watches the AppShell scroll container
// (<main id="main-content">, which owns overflow-y-auto — the page scrolls
// inside it, not the window) and appears once the user scrolls past a
// threshold. Click → smooth-scroll the container back to the top.
//
// Placed slightly above the bottom-right corner (bottom-24) so it clears
// any support/chat FAB that lives in the corner.

import { useEffect, useState, type JSX } from 'react';
import { ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BackToTopProps {
  /** id of the scroll container to watch + scroll. Defaults to AppShell <main>. */
  scrollTargetId?: string;
  /** px scrolled before the button appears. */
  threshold?: number;
}

export function BackToTop({
  scrollTargetId = 'main-content',
  threshold = 400,
}: BackToTopProps): JSX.Element | null {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = document.getElementById(scrollTargetId);
    if (!el) return;
    const onScroll = (): void => setVisible(el.scrollTop > threshold);
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollTargetId, threshold]);

  if (!visible) return null;

  const scrollToTop = (): void => {
    const el = document.getElementById(scrollTargetId);
    el?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const label = t('a11y.backToTop', { defaultValue: 'Back to top' });

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label={label}
      title={label}
      data-testid="back-to-top"
      className="fixed bottom-24 right-6 z-40 inline-flex h-11 w-11 items-center justify-center rounded-full border border-pw-border bg-pw-card text-pw-text-muted shadow-lg transition-colors hover:bg-pw-surface hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
    >
      <ArrowUp className="h-5 w-5" aria-hidden />
    </button>
  );
}

export default BackToTop;
